import { IClauseChecklistImportPreprocessResponse } from "@/api";
import { Box, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Info } from "lucide-react";
import React, { useMemo } from "react";
import { IImportFormData } from "./types";

const ReviewContainer = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3, 2),
    borderRadius: theme.spacing(1),
}));

const ReviewRow = styled(Stack)({
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
});

interface ReviewStepProps {
    formData: IImportFormData;
    reviewStats: IClauseChecklistImportPreprocessResponse;
}

interface ReviewItem {
    label: string;
    value: string;
    ariaLabel: string;
    color?: "success" | "warning" | "error" | "textPrimary";
    tooltip?: string;
}

const getStatusDisplay = (
    count: number,
    type: "success" | "warning" | "error"
): {
    color: "success" | "warning" | "error" | "textPrimary";
} => {
    if (count === 0) return { color: "textPrimary" };

    return {
        color: type,
    };
};

export const ReviewStep: React.FC<ReviewStepProps> = ({
    formData,
    reviewStats,
}) => {
    const reviewItems = useMemo((): ReviewItem[] => {
        const topicsDisplay = getStatusDisplay(
            reviewStats.total_topics,
            "success"
        );
        const invalidTopicsDisplay = getStatusDisplay(
            reviewStats.invalid_topic_names.length,
            "error"
        );
        const enabledClausesDisplay = getStatusDisplay(
            reviewStats.enabled_clause_rows,
            "success"
        );
        const skippedClausesDisplay = getStatusDisplay(
            reviewStats.skipped_clause_rows.length,
            "warning"
        );

        const items: ReviewItem[] = [
            {
                label: "File Name",
                value: formData.file?.name || "No file selected",
                ariaLabel: `Selected file: ${formData.file?.name || "none"}`,
            },
            {
                label: "Checklist Name",
                value: formData.name || "Unnamed checklist",
                ariaLabel: `Checklist name: ${formData.name || "unnamed"}`,
            },
            {
                label: "Organization",
                value: formData.organizationName || "No organization selected",
                ariaLabel: `Target organization: ${formData.organizationName || "none"}`,
            },
            {
                label: "Topics",
                value: `${reviewStats.total_topics} topics`,
                ariaLabel: `${reviewStats.total_topics} topics will be added to the checklist`,
                color: topicsDisplay.color,
            },
        ];

        // Add invalid topics if any
        if (reviewStats.invalid_topic_names.length > 0) {
            items.push({
                label: "Invalid Topics",
                value: `${reviewStats.invalid_topic_names.length} topics`,
                ariaLabel: `${reviewStats.invalid_topic_names.length} topics were not found and will be skipped`,
                color: invalidTopicsDisplay.color,
                tooltip: reviewStats.invalid_topic_names.join(", "),
            });
        }

        // Add enabled clauses info
        items.push({
            label: "Enabled Clause Configs",
            value:
                reviewStats.enabled_clause_rows > 0
                    ? `${reviewStats.enabled_clause_rows} clause-sections`
                    : "All clauses enabled",
            ariaLabel:
                reviewStats.enabled_clause_rows > 0
                    ? `${reviewStats.enabled_clause_rows} specific clause-section configurations`
                    : "All clauses in all topics will be enabled",
            color: enabledClausesDisplay.color,
            tooltip:
                reviewStats.enabled_clause_rows === 0
                    ? "No custom clause selection - all clauses in each topic will be enabled"
                    : undefined,
        });

        // Add skipped clause rows if any
        if (reviewStats.skipped_clause_rows.length > 0) {
            const firstFewReasons = reviewStats.skipped_clause_rows
                .slice(0, 5)
                .map((row) => `Row ${row.row_number}: ${row.reason}`)
                .join("\n");
            const moreCount =
                reviewStats.skipped_clause_rows.length > 5
                    ? `\n...and ${reviewStats.skipped_clause_rows.length - 5} more`
                    : "";

            items.push({
                label: "Skipped Clause Rows",
                value: `${reviewStats.skipped_clause_rows.length} rows`,
                ariaLabel: `${reviewStats.skipped_clause_rows.length} clause rows were skipped due to errors`,
                color: skippedClausesDisplay.color,
                tooltip: firstFewReasons + moreCount,
            });
        }

        return items;
    }, [formData, reviewStats]);

    return (
        <Stack gap={2} role="region" aria-labelledby="review-heading">
            <Typography
                id="review-heading"
                variant="h6"
                sx={{ position: "absolute", left: "-10000px" }}
            >
                Import Review Summary
            </Typography>

            <ReviewContainer>
                <Stack gap={2} divider={<Divider />}>
                    {reviewItems.map((item) => (
                        <ReviewRow key={item.label}>
                            <Typography variant="body2">
                                {item.label}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <Typography
                                    color={item.color || "textPrimary"}
                                    aria-label={item.ariaLabel}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                    variant="body2"
                                    fontWeight={500}
                                >
                                    {item.value}
                                </Typography>
                                {item.tooltip && (
                                    <Tooltip
                                        title={
                                            <Typography
                                                variant="caption"
                                                sx={{ whiteSpace: "pre-line" }}
                                            >
                                                {item.tooltip}
                                            </Typography>
                                        }
                                        arrow
                                    >
                                        <Info size={14} />
                                    </Tooltip>
                                )}
                            </Box>
                        </ReviewRow>
                    ))}
                </Stack>
            </ReviewContainer>
        </Stack>
    );
};
